"use client"
import {
  type PlanId,
  PLAN_NAMES,
  PLAN_NODE_IDS,
  getSelectedPlanFromPath,
} from '@/lib/branchTypes'
import { scrollToChoicePointHeader } from './choicePointAnchor'
import { ScenarioEndFooter } from '../ScenarioEndFooter'

type ReadAnotherBranchProps = {
  selectedPath: string[]
  onSelectBranch: (targetId: string) => void
}

// Plan A is "the recommendation": every non-A path ends by sending the reader
// back to it. Rather than re-render the whole choice-point menu plus Plan A's
// full content inline, the leaf now shows a single "Read Plan A" button that
// selects Plan A and scrolls back up to the top-of-page choice point (now with
// Plan A selected), where its content reads through from the start.
const INLINE_CONTINUATION_PLAN: PlanId = 'A'

// Shown at the end of a leaf branch. On Plan A the end cap is the 2×2
// end-of-scenario nav grid (Public / Insider / Epilogue / choose another plan).
// On every other plan it's a single "Read Plan A" button that scrolls back up
// to the choice point with Plan A selected.
export const ReadAnotherBranch = (props: ReadAnotherBranchProps) => {
  const currentPlan = getSelectedPlanFromPath(props.selectedPath)

  const readPlanA = () => {
    const targetId = PLAN_NODE_IDS[INLINE_CONTINUATION_PLAN]
    if (targetId) props.onSelectBranch(targetId)
    scrollToChoicePointHeader()
  }

  // Finishing the recommended Plan A path lands on the shared
  // end-of-scenario cap (heading + nav grid + reviewer box) — the
  // SAME component every other "you've reached the end" lands on, so
  // the pieces can't drift apart per ending. The nav grid navigates
  // via the URL hash, which the playbook's useAnchorDeepLink already
  // turns into path-expand + epilogue-mount + scroll.
  if (currentPlan === 'A') {
    return <ScenarioEndFooter ending="mainScenario" />
  }

  return (
    <section className="mt-16 mb-12">
      <button
        type="button"
        onClick={readPlanA}
        className="inline-flex items-center gap-6 px-10 mt-8 rounded-md border-4 border-current bg-transparent hover:bg-black/5 transition-colors cursor-pointer font-bold tracking-wide py-6 text-3xl md:text-4xl text-center"
      >
        Read Plan A
        <ArrowRight size={16} />
      </button>
    </section>
  )
}

// Plain rightward arrow trailing the "Read Plan A" label — a shaft into an
// arrowhead, cueing "continue onward" to the right. `size` sets the rendered
// px box; the path lives in a fixed 16-unit viewBox that scales to it.
const ArrowRight = ({ size = 16 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <polyline points="2,8 14,8" />
    <polyline points="9,3 14,8 9,13" />
  </svg>
)
